import { zodResolver } from '@hookform/resolvers/zod';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
	ArrowLeftOutline,
	Button,
	Display,
	HStack,
	ListItem,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Spacer,
	Stack,
	Text,
	UnorderedList,
	useDisclosure,
	useToast,
} from '@bonusly/design-system';
import { assertPresence } from '@bonusly/runtime';

import { Reveal } from '../../components/display/reveal';
import { PageTitle } from '../../components/page/page-title';
import { FORM_PANEL_WIDTH } from '../../config/admin';
import { AwardDetail, AwardState, BudgetPeriod } from '../../entities/awards';
import { GroupType } from '../../entities/groups';
import { Schema } from '../../lib/schema';
import { useAward, useUpdateAward } from '../../queries/awards';
import { AwardForm } from './award-form';
import { DeactivateAwardModal } from './deactivate-award-modal';
import { DeleteAwardModal } from './delete-award-modal';
import { AwardFormData, BudgetMode, MessageMode } from './types';
import { useSchema } from './use-schema';
import { buildFormBody } from './utils';
import { AdminAwardTrackingEvent } from './events';
import { useAnalytics } from '@bonusly/who-am-i';

const getBudgetMode = (record: AwardDetail) => {
	if (record.amount === null && record.budgetPerPeriod === null) {
		return BudgetMode.NoLimit;
	}

	if (record.amount === null && record.budgetPerPeriod !== null) {
		return BudgetMode.FlexibleLimit;
	}

	// Both the amount and budget per period are set
	return BudgetMode.FixedLimit;
};

const getMessageMode = (record: AwardDetail) => {
	if (record.message && record.messageEditable) {
		return MessageMode.Editable;
	}

	if (record.message && !record.messageEditable) {
		return MessageMode.Provided;
	}

	return MessageMode.Custom;
};

export const EditAward: FC = () => {
	const { id } = useParams<{ id: string }>();
	const toast = useToast();
	const navigate = useNavigate();
	const deleteModal = useDisclosure();
	const deactivateModal = useDisclosure();
	const reactivateModal = useDisclosure();
	const confirmModal = useDisclosure();
	const { t } = useTranslation('awards', { keyPrefix: 'edit' });
	const schema = useSchema();
	const { track } = useAnalytics();
	assertPresence(id);
	const { data: record } = useAward(id);
	assertPresence(record);

	const form = useForm<AwardFormData>({
		defaultValues: {
			name: record.name,
			description: record.description ?? '',
			conditions: Schema.serialize<{ attr: string; value: string }[]>(record.conditions ?? [{ attr: undefined, value: undefined }]),
			conditionsOperator: record.conditionsOperator ?? GroupType.Or,
			budgetMode: getBudgetMode(record),
			periodType: record.periodType ?? BudgetPeriod.Month,
			budgetPerPeriod: record.budgetPerPeriod,
			amount: record.amount,
			amountEditable: record.amountEditable,
			icon: record.iconUrl,
			title: record.title ?? '',
			messageMode: getMessageMode(record),
			message: record.message ?? '',
			visibility: record.visibility,
			visibilityEditable: record.visibilityEditable,
			requireApproval: record.approvalLevels.length > 0,
			approvalLevels: structuredClone(record.approvalLevels),
		},
		resolver: zodResolver(schema),
	});

	const update = useUpdateAward(record, {
		onSuccess: () => {
			toast({
				title: t('updateSuccess'),
				status: 'success',
			});
			navigate('/admin/programs/awards');
		},
		onError: () => {
			toast({
				title: t('updateFailure'),
				status: 'error',
			});
		},
	});

	const reactivate = useUpdateAward(record, {
		onSuccess: () => {
			toast({
				title: t('reactivateSuccess'),
				status: 'success',
			});
			navigate('/admin/programs/awards');
		},
		onError: () => {
			toast({
				title: t('reactivateError'),
				status: 'error',
			});
		},
	});

	const handleConfirmUpdate = () => {
		confirmModal.onOpen();
	};

	const handleSubmitForm = () => {
		const currentValues = form.getValues();
		update.mutate(buildFormBody(currentValues));
	};

	const handleReactivate = () => {
		const currentValues = form.getValues();
		reactivate.mutate(
			buildFormBody({
				...currentValues,
				state: AwardState.Active,
			})
		);
		track(AdminAwardTrackingEvent.ReactivateAward, { awardId: record.id });
	};

	return (
		<>
			<PageTitle>{t('title')}</PageTitle>

			<Stack px={10} py={8} spacing={6} maxW={FORM_PANEL_WIDTH}>
				<HStack>
					<Button as={Link} variant="link" gap={1} to="/admin/programs/awards" type="button">
						<ArrowLeftOutline size="xs" />
						{t('back')}
					</Button>
				</HStack>

				<Display size="sm">{t('description')}</Display>

				<AwardForm form={form} isLoading={update.isLoading} onSubmit={handleConfirmUpdate}>
					<HStack justify="space-between" width="100%">
						<Reveal if={record.isActive}>
							<Button variant="alpha" colorScheme="secondary" onClick={deactivateModal.onOpen}>
								{t('deactivate')}
							</Button>
							<Button type="submit" isLoading={update.isLoading} isDisabled={!form.formState.isDirty}>
								{t('saveChanges')}
							</Button>
						</Reveal>

						<Reveal if={!record.isActive}>
							<Button variant="alpha" colorScheme="secondary" onClick={deleteModal.onOpen}>
								{t('delete')}
							</Button>
							<Button onClick={reactivateModal.onOpen} isLoading={reactivate.isLoading}>
								{t('reactivate')}
							</Button>
						</Reveal>
					</HStack>
				</AwardForm>
			</Stack>

			<DeleteAwardModal award={record} isOpen={deleteModal.isOpen} onClose={deleteModal.onClose} onSuccess={() => navigate('/admin/programs/awards')} />

			<DeactivateAwardModal
				award={record}
				isOpen={deactivateModal.isOpen}
				onClose={deactivateModal.onClose}
				onSuccess={() => navigate('/admin/programs/awards')}
			/>

			<ReactivateModal
				award={record}
				form={form}
				isLoading={reactivate.isLoading}
				isOpen={reactivateModal.isOpen}
				onClose={reactivateModal.onClose}
				onConfirm={handleReactivate}
			/>

			<ConfirmUpdateModal form={form} isLoading={update.isLoading} isOpen={confirmModal.isOpen} onClose={confirmModal.onClose} onConfirm={handleSubmitForm} />
		</>
	);
};

type FieldSet = keyof AwardFormData;

const fieldMap: Partial<Record<FieldSet, string>> = {
	name: 'confirmName',
	description: 'confirmDescription',
	conditionsOperator: 'confirmGivers',
	conditions: 'confirmGivers',
	budgetMode: 'confirmBudget',
	budgetPerPeriod: 'confirmBudget',
	periodType: 'confirmBudget',
	amount: 'confirmAmount',
	icon: 'confirmIcon',
	title: 'confirmTitle',
	messageMode: 'confirmMessage',
	message: 'confirmMessage',
	visibility: 'confirmVisibility',
	visibilityEditable: 'confirmVisibility',
	requireApproval: 'confirmApproval',
	approvalLevels: 'confirmApproval',
};

type ReactivateModalProps = {
	award: AwardDetail;
	isOpen: boolean;
	onClose: () => void;
	form?: ReturnType<typeof useForm<AwardFormData>>;
	onConfirm: () => void;
	isLoading: boolean;
};

const ReactivateModal: FC<ReactivateModalProps> = ({ award, isOpen, onClose, form, onConfirm, isLoading }) => {
	const { t } = useTranslation('awards', { keyPrefix: 'edit' });

	const edited = form
		? Object.entries(fieldMap)
				.filter(([key]) => !!form.formState.dirtyFields[key as FieldSet])
				.map(([, key]) => t(key))
		: [];

	const keys = [...new Set(edited)];

	const hasChanges = keys.length > 0;

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="lg">
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>{t('reactivateTitle')}</ModalHeader>
				<ModalCloseButton />
				<ModalBody p={5}>
					<Stack spacing={4}>
						<Text fontWeight="semibold">{t('reactivateSubtitle', { name: award.name })}</Text>
						<Reveal if={hasChanges}>
							<Text>{t('reactivateDescription')}</Text>
							<UnorderedList pl={2}>
								{keys.map((key, i) => (
									<ListItem key={i}>{key}</ListItem>
								))}
							</UnorderedList>
						</Reveal>
						<Text>{t('reactivateDescription2')}</Text>
					</Stack>
				</ModalBody>

				<ModalFooter>
					<Button variant="alpha" colorScheme="secondary" mr={2} onClick={onClose}>
						{t('cancelButtonLabel')}
					</Button>
					<Spacer />
					<Button colorScheme="primary" variant="solid" isLoading={isLoading} onClick={onConfirm}>
						{t('reactivateButtonLabel')}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

type ConfirmUpdateModalProps = {
	form: ReturnType<typeof useForm<AwardFormData>>;
	isLoading: boolean;
	isOpen: boolean;
	onClose: () => void;
	onConfirm: () => void;
};

const ConfirmUpdateModal: FC<ConfirmUpdateModalProps> = ({ form, isLoading, isOpen, onClose, onConfirm }) => {
	const { t } = useTranslation('awards', { keyPrefix: 'edit' });
	const edited = Object.entries(fieldMap)
		.filter(([key]) => !!form.formState.dirtyFields[key as FieldSet])
		.map(([, key]) => key);

	const keys = [...new Set(edited)];

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="lg">
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>{t('confirm.header')}</ModalHeader>
				<ModalCloseButton />
				<ModalBody p={5}>
					<Text mb={2}>{t('confirm.description')}</Text>
					<UnorderedList pl={2}>
						{keys.map((key, i) => (
							<ListItem key={i}>{t(key)}</ListItem>
						))}
					</UnorderedList>
					<Text mt={2}>{t('confirm.description2')}</Text>
				</ModalBody>

				<ModalFooter>
					<Button variant="alpha" colorScheme="secondary" mr={2} onClick={onClose}>
						{t('confirm.cancel')}
					</Button>
					<Spacer />
					<Button isLoading={isLoading} onClick={onConfirm}>
						{t('confirm.confirm')}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
